<template>
  <!--
    The header no longer carries its own `data-drag-handle` — the whole
    Detail-mode `<article>` on `PlayerCard` is the drag handle, so a
    gesture can start anywhere on the card background. Each interactive
    child here (name fields, tag chips/buttons) marks itself
    `data-no-drag` so a press on those is never mis-read as a drag
    intent. SortableJS keys off both via `handle: '[data-drag-handle]'`
    + `filter: '[data-no-drag]'` in `useDragAndDrop`.
  -->
  <header class="flex min-w-0 flex-1 flex-col gap-1">
    <div class="flex min-w-0 items-center justify-between gap-2">
      <span
        class="min-w-0 flex-1 wrap-break-word text-left text-lg font-bold leading-tight text-text-primary sm:text-xl"
        :title="displayName"
      >{{ displayName }}</span>
      <RoleBadge
        v-if="!isRaid"
        :role="cardRole"
        :left-path="leftPathColor"
        :right-path="rightPathColor"
      />
    </div>
    <div class="flex min-w-0 items-center justify-between gap-2">
      <button
        v-if="canManageRsvp"
        data-no-drag
        type="button"
        class="inline-flex min-h-6 shrink-0 items-center gap-1 rounded-full border border-border-strong bg-bg-elevated px-2 text-xs font-semibold text-accent transition-[border-color,color,opacity] duration-120 hover:border-accent hover:text-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-wait disabled:opacity-60"
        :disabled="rsvpPending"
        :aria-busy="rsvpPending ? 'true' : 'false'"
        :aria-label="rsvpActionLabel"
        :title="signState.label"
        @click="emit('rsvp-toggle')"
      >
        <span
          class="h-1.5 w-1.5 rounded-full"
          :class="player.hasSignedIn ? 'bg-signed-point' : 'bg-text-muted'"
          aria-hidden="true"
        />
        {{ rsvpActionText }}
      </button>
      <span
        v-else
        class="inline-flex shrink-0 items-center gap-1 text-xs font-semibold"
        :class="player.hasSignedIn ? 'text-success' : 'text-text-muted'"
        :aria-label="signState.label"
      >
        <span
          class="h-1.5 w-1.5 rounded-full"
          :class="player.hasSignedIn ? 'bg-signed-point' : 'bg-text-muted'"
          aria-hidden="true"
        />
        {{ signState.text }}
      </span>
      <div v-if="canEdit" class="flex min-w-0 flex-1 items-center justify-end gap-1 text-right">
        <span class="shrink-0 font-mono text-xs uppercase tracking-wide text-text-muted">{{ messages.player.inGame }}</span>
        <PlayerNameField
          :value="player.inGameName"
          :can-edit="canEdit"
          @commit="(value) => emit('name-commit', value)"
        />
      </div>
    </div>
    <p v-if="canManageRsvp && rsvpError" role="alert" class="text-xs text-danger">
      {{ rsvpError }}
    </p>
    <PlayerTagStrip
      :player="player"
      :player-name-mode="store.playerNameMode"
      :can-edit="canEditTags"
      :is-active-tag-filter="isActiveTagFilter"
      :assignable-tag-options="assignableTagOptions"
      :assign-pending="assignPending"
      :assign-error="assignError"
      @toggle="(id) => emit('tag-toggle', id)"
      @assign="(id) => emit('tag-assign', id)"
      @unassign="(id) => emit('tag-unassign', id)"
    />
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import PlayerNameField from './PlayerNameField.vue'
import PlayerTagStrip from '../tags/PlayerTagStrip.vue'
import RoleBadge from './RoleBadge.vue'
import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import type { Player, PlayerTag } from '../../types'
import { CardRole } from '../../lib/roleLabels'
import { playerDisplayName } from '../../lib/playerIdentity'
import type { WeaponRoleColor } from '@/types/weapon'
import { PlayerRole } from '@/types/weapon'
import type { ViewMode } from '../../types'

export interface PlayerCardHeaderProps {
  readonly player: Player
  /** View mode forwarded for parity with the parent card. The header itself is detail-mode only. */
  readonly mode: ViewMode
  /** Raid snapshots own a per-raid Discord RSVP instead of the live-board OAuth status. */
  readonly isRaid: boolean
  /** Permission gate (officer + Live board mode). Owned by the parent card; the header is a pure consumer. */
  readonly canEdit: boolean
  /** Permission gate for player tags, which remain editable in raid snapshots. */
  readonly canEditTags: boolean
  /** Left half of the role badge — main weapon #1's school color. */
  readonly leftPathColor: WeaponRoleColor | null
  /** Right half of the role badge — main weapon #2's school color (falls back to `leftPathColor` when only one is equipped). */
  readonly rightPathColor: WeaponRoleColor | null
  /** Tailwind class string for the left full-height path strip on the card. Header doesn't render the strip itself but accepts the value for symmetry. */
  readonly leftStripClass: string
  /** Tailwind class string for the right full-height path strip on the card. Same as `leftStripClass`. */
  readonly rightStripClass: string
  /**
   * Forwarded to `<PlayerTagStrip>`: `true` while the parent card is
   * awaiting `store.assignTag`. While pending the strip greys out its
   * dropdown options and the `+ Tag` trigger, preventing double submits
   * inside the request window.
   */
  readonly assignPending: boolean
  /**
   * Forwarded to `<PlayerTagStrip>`: inline accessible error message
   * surfaced when the awaited `assignTag` Promise resolves `false`.
   * `null` hides the error region. Owned by the parent card so the
   * strip stays purely presentational.
   */
  readonly assignError: string | null
  /** Whether this card represents the signed-in user on a raid snapshot. */
  readonly canManageRsvp: boolean
  /** Mutation acknowledged but the authoritative subscription snapshot has not arrived yet. */
  readonly rsvpPending: boolean
  /** Card-local RSVP error; does not get replaced by unrelated store mutations. */
  readonly rsvpError: string | null
}

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const props = defineProps<PlayerCardHeaderProps>()
const store = useGvgPlannerStore()

interface SignState {
  readonly text: string
  readonly label: string
}

const signState = computed<SignState>(() => {
  if (props.isRaid) {
    return props.player.hasSignedIn
      ? { text: messages.value.player.signedUp, label: messages.value.player.signedUpAria }
      : { text: messages.value.player.notSignedUp, label: messages.value.player.notSignedUpAria }
  }
  return props.player.hasSignedIn
    ? { text: messages.value.player.signedIn, label: messages.value.player.signedInAria }
    : { text: messages.value.player.notSigned, label: messages.value.player.notSignedAria }
})

const rsvpActionText = computed<string>(() => {
  if (props.rsvpPending) return messages.value.player.rsvpPending
  return props.player.hasSignedIn
    ? messages.value.player.signOutRaid
    : messages.value.player.signInRaid
})

const rsvpActionLabel = computed<string>(() => props.player.hasSignedIn
  ? messages.value.player.signOutRaidAria
  : messages.value.player.signInRaidAria,
)

const displayName = computed<string>(() =>
  playerDisplayName(props.player, store.playerNameMode),
)

/**
 * Pure-presentational surface. The header holds no local state machinery
 * (refs, watchers, lifecycle hooks); the only `computed`s are derived
 * from the props + the Pinia store so child components can stay dumb.
 *
 * `useGvgPlannerStore()` is read here only for tag-list derivation; the
 * header does NOT dispatch mutations — every mutation is re-emitted via
 * `defineEmits` and handled by the parent card.
 */
/**
 * The server persists the exact role derived from the equipped main weapons.
 * A player with no selected weapons is the one presentation-only exception.
 *
 * `PLAYER_ROLE_TO_CARD` is keyed by `PlayerRole` so `noUncheckedIndexedAccess`
 * + the `Readonly<Record<...>>` shape guarantee every variant is mapped —
 * adding a new `PlayerRole` member becomes a compile error here as well as
 * at the switch. A `Record` lookup keeps the mapping exhaustive and the
 * function body return-coverage obvious to the IDE without resorting to a
 * final `return` past a `switch` (which `allowUnreachableCode: false` rejects)
 * or an `assertNever` helper that would still need a defensive arm.
 */
const PLAYER_ROLE_TO_CARD: Readonly<Record<PlayerRole, CardRole>> = {
  [PlayerRole.Tank]: CardRole.Tank,
  [PlayerRole.Dps]: CardRole.Dps,
  [PlayerRole.Hybrid]: CardRole.Hybrid,
  [PlayerRole.Heal]: CardRole.Heal,
}

const cardRole = computed<CardRole>(() => {
  const p = props.player
  if (p.mainWeapons.length === 0) return CardRole.Nameless
  return PLAYER_ROLE_TO_CARD[p.role]
})

/**
 * Tag catalog minus the tags this player already carries. Used by the
 * `+ Tag` dropdown so officers can't re-assign what's already pinned.
 * Mirrors the original `PlayerCard.assignableTagOptions`.
 */
const assignableTagOptions = computed<ReadonlyArray<PlayerTag>>(() => {
  const owned = new Set(props.player.tags.map((t) => t.id))
  return store.playerTags.filter((t) => !owned.has(t.id))
})

function isActiveTagFilter(tagId: string): boolean {
  return store.isActiveTagFilter(tagId)
}

/**
 * Emit shape:
 *   - `tag-toggle`    — a pinned tag chip was clicked.
 *   - `tag-assign`    — a tag was picked from the `+ Tag` listbox.
 *   - `tag-unassign`  — the officer-only `×` button on a pinned chip
 *     was clicked (forwarded from `<PlayerTagStrip>`).
 *   - `name-commit`   — in-game name committed (forwarded from `<PlayerNameField>`).
 *
 * The `assignPending` + `assignError` flags used by `<PlayerTagStrip>`
 * are NOT events — they're inbound props forwarded from the parent
 * card. The card owns the awaited-`assignTag` lifecycle and pushes the
 * resulting pending / error state back into the header (and through it,
 * the strip) via props.
 */
const emit = defineEmits<{
  (e: 'tag-toggle', tagId: string): void
  (e: 'tag-assign', tagId: string): void
  (e: 'tag-unassign', tagId: string): void
  (e: 'name-commit', value: string): void
  (e: 'rsvp-toggle'): void
}>()
</script>
